import { pickT } from "@repo/shared";
import type { LocaleCode, PublicFlowLanguage } from "@repo/shared/types";
import { AlertCircle, CheckCircle2, FileText, Loader2, Pen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import SignatureCanvas from "react-signature-canvas";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, apiErrorMessage, get, post } from "@/lib/api/_client";
import { languageFromError } from "@/lib/api/public-flow/contact-info";
import { T } from "./page.i18n";

// The order row plus the flow's `language` (QL-17 issue 027).
type SignatureOrderData = (Record<string, any> & PublicFlowLanguage) | null;

export default function SimpleSignaturePage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const signToken = params.token as string;
  const sigCanvas = useRef<SignatureCanvas>(null);

  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [signatureOrder, setSignatureOrder] = useState<SignatureOrderData>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  // Translations in the flow's language: from the order, or from a refused
  // link's error body; English only when no response came back.
  const [errorLanguage, setErrorLanguage] = useState<LocaleCode | null>(null);
  const flowLang: LocaleCode = signatureOrder?.language ?? errorLanguage ?? "en_GB";
  const t = pickT(T, flowLang);

  // Check for session and load signature order
  useEffect(() => {
    // First, load signature order to get public token
    // The API writes a refused link in the flow's language (QL-17) and says
    // which one. The fallback (network failure) is English: no flow language.
    let loadError: string | null = null;
    get<SignatureOrderData>(`/public/signatures/${signToken}`)
      .catch((err) => {
        const language = languageFromError(err);
        setErrorLanguage(language);
        loadError = apiErrorMessage(err, pickT(T, language ?? "en_GB").invalidOrExpired);
        return null as SignatureOrderData;
      })
      .then((data) => {
        if (!data) {
          setError(loadError ?? T.en_GB.invalidOrExpired); // network failure: no language
          setLoading(false);
          return;
        }

        if (data.signatureMethod !== "simple") {
          setError(pickT(T, data.language).notSimpleSignature);
          setLoading(false);
          return;
        }

        // Check if session token is in URL (from Criipto callback or auth page)
        const urlSessionToken = searchParams.get("session");

        if (urlSessionToken) {
          // Store it in localStorage and remove from URL
          localStorage.setItem("publicSessionToken", urlSessionToken);
          // Remove session param from URL
          const newUrl = window.location.pathname;
          window.history.replaceState({}, "", newUrl);
        }

        // Check if we have a session token
        const token = urlSessionToken || localStorage.getItem("publicSessionToken");

        if (!token) {
          // Redirect to auth page with the flow instance's public token
          if (data.flowInstance.publicToken) {
            // Pass returnTo parameter so auth redirects back to sign-simple
            navigate(
              `/p/${data.flowInstance.publicToken}/auth?returnTo=/p/${signToken}/sign-simple`,
            );
          } else {
            setError(pickT(T, data.language).authNotAvailable);
            setLoading(false);
          }
          return;
        }

        setSessionToken(token);

        if (data.status === "signed") {
          setSuccess(true);
        }

        setSignatureOrder(data);
        setLoading(false);
      });
  }, [signToken, navigate, searchParams]);

  // Redirect to completion page when signature is successful
  useEffect(() => {
    if (success && signatureOrder) {
      // Store client name and flow language, then redirect to signature completion page
      if (signatureOrder.flowInstance?.client?.name) {
        sessionStorage.setItem("signatureClientName", signatureOrder.flowInstance.client.name);
      }
      sessionStorage.setItem("signatureFlowLanguage", flowLang);
      navigate("/p/signature-complete");
    }
  }, [success, signatureOrder, navigate, flowLang]);

  const handleClear = () => {
    sigCanvas.current?.clear();
    setIsEmpty(true);
  };

  const handleSign = async () => {
    if (isEmpty || !sigCanvas.current || !sessionToken) {
      setError(t.pleaseProvideSignature);
      return;
    }

    setSigning(true);
    setError(null);

    try {
      // Get signature as data URL
      const signatureDataUrl = sigCanvas.current.toDataURL("image/png");

      // Submit signature
      await post(`/public/signatures/${signToken}/complete`, { signatureDataUrl, sessionToken });

      setSuccess(true);
      setSigning(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || t.failedToComplete);
      } else {
        setError(t.errorOccurred);
      }
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !signatureOrder) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || t.failedToLoad}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Pen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{t.signDocuments}</h1>
              <p className="text-sm text-muted-foreground">
                {signatureOrder.flowInstance.client.name}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* PDFs to sign */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t.documentsToSign}
            </CardTitle>
            <CardDescription>{t.documentsRequireSignature}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(signatureOrder.pdfLogs ?? []).map((pdf: any) => (
                <div
                  key={pdf.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{pdf.pdfLog.template.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {pdf.pdfLog.fileSize ? (pdf.pdfLog.fileSize / 1024).toFixed(1) : "0"} KB
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <a href={pdf.pdfLog.s3Url || "#"} target="_blank" rel="noopener noreferrer">
                      {t.viewPdf}
                    </a>
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Signature Canvas */}
        <Card>
          <CardHeader>
            <CardTitle>{t.yourSignature}</CardTitle>
            <CardDescription>{t.signInBox}</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div className="border-2 border-dashed border-muted-foreground/20 rounded-lg overflow-hidden bg-white">
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="black"
                  backgroundColor="white"
                  canvasProps={{
                    style: { width: "100%", height: "192px" },
                    className: "cursor-crosshair",
                  }}
                  onBegin={() => setIsEmpty(false)}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={handleClear} disabled={signing}>
                  {t.clear}
                </Button>
                <Button
                  onClick={handleSign}
                  disabled={isEmpty}
                  className="sm:flex-1"
                  loading={signing}
                >
                  {t.signDocumentsButton}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">{t.legallyBinding}</p>
            </div>
          </CardContent>
        </Card>

        {/* Evidence info */}
        <Card className="border-success/20 bg-success/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-success mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">{t.signatureEvidence}</p>
                <p className="text-xs text-muted-foreground">{t.signatureStoredWith}</p>
                <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1 mt-2">
                  <li>{t.dateAndTime}</li>
                  <li>{t.authMethod}</li>
                  <li>{t.contactVerified}</li>
                  <li>{t.digitalSignature}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
